import React, {
  Fragment,
  Dispatch,
  SetStateAction,
  useState,
  ChangeEvent,
  ReactNode,
  useReducer
} from 'react'
import {
  Grid,
  Container,
  Paper,
  Button,
  TextField,
  Typography,
  Box,
  Tooltip,
  IconButton,
  CardMedia,
  ButtonGroup,
  useMediaQuery,
  useTheme
} from '@mui/material'
import { GenericTableV2 } from '../_EOS/GenericTable/GenericTableV2'
import {
  createPickingValidateColumns,
  createPickingValidateActions
} from './PickingValidateTableConfig'
import { BatchesModal } from './BatchesModal'
import { useStyles } from './styles'
import {
  PickingValidateHeader,
  PickingValidateSkuDetail
} from '../../interfaces/Picking/Picking'
import { Loading } from '../../components/UI'
import { Modal } from '../Modal/Modal'
import { Modal as EOSModal } from '../_EOS/Modal'
import { barCode } from '../../images/barCode'

import { clsAlphaNoOnly } from '../../utils/regex/regexFunctions'
import { DocumentPrint } from '../UI/DocumentPrint/DocumentPrint'
import Info from '@mui/icons-material/Info'
import ClearIcon from '@mui/icons-material/Clear'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import ReportProblemIcon from '@mui/icons-material/ReportProblem'
import SettingsIcon from '@mui/icons-material/Settings'
import { useModal } from '../../hooks/useModal'
import { ProductValidation } from './ProductValidationModal/ProductValidation'
import { CloseValidationConfirmationModal } from './Modals/CloseValidationConfirmationModal'
import { ConfirmAction } from '../ConfirmAction'
import { eTypePickingRoute } from '../../enums/eTypePickingRoute'
import { DateTime } from 'luxon'
import { PickingDiffComponent } from './PickingDiff/PickingDiffComponent'
import { PickingValidateExcessQty } from './PickingValidateExcessQty'
import {
  reducerInitialState,
  pickingValidateReducer
} from './PickingValidateReducer'
import { setShowModalReportDiff } from './PickingValidateAction'
import { PickingDiffereceExplain } from './PickingDiff/PickingDiffereceExplain'

interface PickingValidateRouteProps {
  handleOnChangeFilter: (e: any) => void
  setBtnFilter: Dispatch<SetStateAction<string>>
  setSkuId: Dispatch<SetStateAction<string>>
  handleStartPickingRoute: (field?: string, value?: string) => void
  handleOnClickSorter: (name: string, sorter: string) => void
  handlePrintPickingRouteValidate: () => void
  handleClosePickingRouteValidate: () => void
  handlePagination: (updatePage: number) => void
  handlePageSize: (
    event: ChangeEvent<{ name?: string | undefined; value: unknown }>,
    child: ReactNode
  ) => void
  rows: PickingValidateSkuDetail[] | undefined
  isLoadingGrid: boolean
  btnFilter: string
  sorters: any[]
  isLoadingPrint: boolean
  pickingValidatePDF: string
  pickingRouteId: number
  page: number
  totalItems: number
  pageSize: number
  isLoadingConfirm: boolean
  skuId: string
  pickingHeader: PickingValidateHeader
  isPickingRouteGroupedValidation: boolean
}

export const PickingValidateRoute: React.FC<PickingValidateRouteProps> = ({
  setBtnFilter,
  handleStartPickingRoute,
  handleOnClickSorter,
  handlePrintPickingRouteValidate,
  handlePagination,
  handlePageSize,
  handleClosePickingRouteValidate,
  setSkuId,
  rows,
  isLoadingGrid,
  sorters,
  isLoadingPrint,
  pickingValidatePDF,
  pickingRouteId,
  page,
  totalItems,
  pageSize,
  pickingHeader,
  isLoadingConfirm,
  isPickingRouteGroupedValidation
}) => {
  const validationModal = useModal(
    'Validación de Productos',
    !isPickingRouteGroupedValidation,
    'md'
  )
  const classes = useStyles()
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))
  const [isOpen, setIsOpen] = useState<boolean>(false)
  const [isOpenConfirmation, setIsOpenConfirmation] = useState<boolean>(false)
  const [sku, setSku] = useState<string>('')

  // Estados para modales de la tabla
  const [showModalImage, setShowModalImage] = useState<boolean>(false)
  const [showModalExcessQty, setShowModalExcessQty] = useState<boolean>(false)
  const [showModalBatches, setShowModalBatches] = useState<boolean>(false)
  const [selectedRow, setSelectedRow] =
    useState<PickingValidateSkuDetail | null>(null)
  const [tableState, dispatch] = useReducer(
    pickingValidateReducer,
    reducerInitialState
  )
  const { showModalReportDiff } = tableState
  const {
    totalSku,
    realQty,
    pickedQty,
    workOrderIdList,
    courierDeliveredDate,
    picker,
    zoneName,
    orderCancellationRequest,
    allowToValidate,
    typePickingRouteId
  } = pickingHeader

  const handleClose = () => {
    setIsOpen(false)
  }

  const handlenOpen = () => {
    handlePrintPickingRouteValidate()
    setIsOpen(true)
  }

  const handleCloseConfirmation = () => {
    setIsOpenConfirmation(false)
  }

  const handleOpenConfirmation = () => {
    setIsOpenConfirmation(true)
  }

  const handleChangeSku = (e: any) => {
    setSku(e.target.value)
  }

  // Handlers para la tabla y modales
  const handleOpenReportDiff = (row: PickingValidateSkuDetail) => {
    setSelectedRow(row)
    dispatch(setShowModalReportDiff(true))
  }

  const handleCloseModalReportDiff = (savedData: boolean) => {
    if (savedData) {
      handleStartPickingRoute(
        'pickingRouteId',
        selectedRow?.pickingRouteId.toString()
      )
    }
    dispatch(setShowModalReportDiff(false))
    setSelectedRow(null)
  }

  const handleOpenExcessQty = (row: PickingValidateSkuDetail) => {
    setSelectedRow(row)
    setShowModalExcessQty(true)
  }

  const handleCloseModalExcessQty = (trySave: boolean, error?: string) => {
    setShowModalExcessQty(false)
    setSelectedRow(null)
    if (trySave) {
      // Aquí podrías agregar snackbar notification si es necesario
      console.log(error || 'Cantidad actualizada')
    }
  }

  const handleOpenImageModal = (row: PickingValidateSkuDetail) => {
    setSelectedRow(row)
    setShowModalImage(true)
  }

  const handleCloseImageModal = () => {
    setShowModalImage(false)
    setSelectedRow(null)
  }

  const handleOpenBatchesModal = (row: PickingValidateSkuDetail) => {
    setSelectedRow(row)
    setShowModalBatches(true)
  }

  const handleCloseBatchesModal = () => {
    setShowModalBatches(false)
    setSelectedRow(null)
  }

  // Handler para cambio de paginación
  const handlePaginationChange = (newPage: number, newPageSize: number) => {
    if (newPageSize !== pageSize) {
      // Cambio de pageSize: cambiar tamaño y resetear página
      handlePageSize(
        { target: { value: newPageSize } } as ChangeEvent<{ value: unknown }>,
        null
      )
    } else {
      // Solo cambio de página: mantener 0-indexed
      handlePagination(newPage)
    }
  }

  const localCourierDeliveryDate = !courierDeliveredDate
    ? null
    : DateTime.fromISO(courierDeliveredDate.toString(), {
        zone: 'utc'
      }) // Interpreta como UTC
        .setZone('local') // Convierte a la zona local del navegador
        .toFormat('dd/MM/yyyy')

  // Configuración de columnas y acciones para la tabla
  const tableColumns = createPickingValidateColumns({
    isPickingRouteGroupedValidation,
    orderCancellationRequest,
    onOpenReportDiff: handleOpenReportDiff,
    onOpenExcessQty: handleOpenExcessQty,
    onOpenImageModal: handleOpenImageModal,
    onOpenBatches: handleOpenBatchesModal
  })

  const tableActions = createPickingValidateActions({
    isPickingRouteGroupedValidation,
    orderCancellationRequest,
    onOpenReportDiff: handleOpenReportDiff,
    onOpenExcessQty: handleOpenExcessQty,
    onOpenImageModal: handleOpenImageModal,
    onOpenBatches: handleOpenBatchesModal
  })

  // Convertir sorters al formato de GenericTableV2
  const currentSorting =
    sorters.length > 0
      ? {
          field: 'sku' as keyof PickingValidateSkuDetail, // Mapear según orderHeader
          direction: sorters[0].value.includes('asc')
            ? ('asc' as const)
            : ('desc' as const),
          onSortChange: (
            field: keyof PickingValidateSkuDetail,
            direction: 'asc' | 'desc'
          ) => {
            const sorterValue =
              direction === 'asc' ? `${field}_asc` : `${field}_desc`
            handleOnClickSorter(field as string, sorterValue)
          }
        }
      : undefined

  // se puede dar el caso que tengan diferente referenceId por lo que se deja lista.
  const referenceIdsString: string = (pickingHeader.skuDetail || [])
    .map((item) => item.referenceId)
    .filter((refId) => refId && refId.trim() !== '')
    .join(', ')

  return (
    <Fragment>
      <Container maxWidth='xl'>
        <Fragment>
          <Paper elevation={2} style={{ padding: '20px' }}>
            <Grid
              container
              direction='row'
              justifyContent='flex-start'
              alignItems='flex-end'
            >
              <Grid item xs={12} lg={8}>
                <Typography variant='h4' component='h4'>
                  <b>
                    Ruta #
                    {typePickingRouteId === eTypePickingRoute.RTV
                      ? `RTV${pickingRouteId}`
                      : pickingRouteId}{' '}
                    {orderCancellationRequest && (
                      <span style={{ color: '#d32f2f' }}>
                        (SOLICITUD DE CANCELACIÓN)
                      </span>
                    )}
                  </b>
                </Typography>

                <Box mt={2}>
                  <Typography variant='h6' component='h4'>
                    <b>ZONA:</b> {`${zoneName}`}
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12} lg={2}>
                <Button
                  type='submit'
                  variant='contained'
                  color='primary'
                  className={classes.button}
                  onClick={handleOpenConfirmation}
                  disabled={orderCancellationRequest ? !allowToValidate : false}
                >
                  {'GUARDAR'}
                </Button>
                {orderCancellationRequest && (
                  <Tooltip
                    title='Se requiere devolver los productos a sus posiciones originales para su cancelación,
                                  use el botón “Devolver Stock” para realizar los movimientos,
                                  una vez que todos los SKU de la ruta esté con cantidad en 0 se habilitará el botón para continuar'
                  >
                    <IconButton>
                      <Info />
                    </IconButton>
                  </Tooltip>
                )}
              </Grid>
              <Grid item xs={12} lg={2}>
                <Button
                  variant='contained'
                  color='secondary'
                  className={`${classes.button} ${classes.manage}`}
                  onClick={() => {
                    handlePrintPickingRouteValidate()
                    handlenOpen()
                  }}
                >
                  {'Imprimir'}
                </Button>
                <Button
                  variant='contained'
                  className={`${classes.button} ${classes.secondary}`}
                  style={{ display: 'none' }}
                >
                  {'Bitacora'}
                </Button>
                {!isPickingRouteGroupedValidation && (
                  <Button
                    variant='contained'
                    color='inherit'
                    className={`${classes.button} ${classes.manage}`}
                    onClick={() => validationModal.openModal('md')}
                  >
                    {'Validar'}
                  </Button>
                )}
              </Grid>
            </Grid>
          </Paper>

          <Box mt={2}>
            <Paper elevation={2} style={{ padding: '20px' }}>
              <div style={{ padding: '10px' }}>
                <Grid
                  container
                  direction='row'
                  justifyContent='flex-end'
                  alignItems='flex-end'
                >
                  <Grid item xs={12} md={6} lg={3}>
                    <Box display='flex' alignItems='center'>
                      <Typography
                        variant='subtitle1'
                        component='span'
                        style={{
                          minWidth: '120px',
                          fontWeight: 'bold'
                        }}
                      >
                        OT:
                      </Typography>
                      <Typography variant='body2' component='span'>
                        {workOrderIdList.length > 0
                          ? `${
                              typePickingRouteId === eTypePickingRoute.RTV
                                ? 'RTV'
                                : ''
                            }${workOrderIdList.join('-')}`
                          : '-'}
                      </Typography>
                    </Box>
                    <Box display='flex' alignItems='center'>
                      <Typography
                        variant='subtitle1'
                        component='span'
                        style={{
                          minWidth: '120px',
                          fontWeight: 'bold'
                        }}
                      >
                        LPN:
                      </Typography>
                      <Typography variant='body2' component='span'>
                        {pickingHeader.lpn || '-'}
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={12} md={6} lg={3}>
                    <Box display='flex' alignItems='center'>
                      <Typography
                        variant='subtitle1'
                        component='span'
                        style={{
                          minWidth: '120px',
                          fontWeight: 'bold'
                        }}
                      >
                        ID de Pedido:
                      </Typography>
                      <Typography variant='body2' component='span'>
                        {referenceIdsString}
                      </Typography>
                    </Box>
                    <Box display='flex' alignItems='center'>
                      <Typography
                        variant='subtitle1'
                        component='span'
                        style={{
                          minWidth: '120px',
                          fontWeight: 'bold'
                        }}
                      >
                        FALTANTE:
                      </Typography>
                      <Typography
                        variant='body2'
                        component='span'
                        style={{
                          color:
                            realQty - pickedQty === 0 ? '#4caf50' : '#d32f2f',
                          fontWeight: 'bold'
                        }}
                      >
                        {realQty - pickedQty}
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={12} md={6} lg={3}>
                    <Box display='flex' alignItems='center'>
                      <Typography
                        variant='subtitle1'
                        component='span'
                        style={{
                          minWidth: '80px',
                          fontWeight: 'bold'
                        }}
                      >
                        SKUs:
                      </Typography>
                      <Typography
                        variant='body2'
                        component='span'
                        style={{ fontWeight: '500' }}
                      >
                        {totalSku}
                      </Typography>
                    </Box>
                    <Box display='flex' alignItems='center'>
                      <Typography
                        variant='subtitle1'
                        component='span'
                        style={{
                          minWidth: '80px',
                          fontWeight: 'bold'
                        }}
                      >
                        QTY:
                      </Typography>
                      <Typography
                        variant='body2'
                        component='span'
                        style={{ fontWeight: '500' }}
                      >
                        {realQty}
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid
                    item
                    xs={12}
                    md={6}
                    lg={3}
                    style={{ alignSelf: 'flex-start' }}
                  >
                    {localCourierDeliveryDate && (
                      <Box display='flex' alignItems='center'>
                        <Typography
                          variant='subtitle1'
                          component='span'
                          style={{
                            minWidth: '120px',
                            fontWeight: 'bold'
                          }}
                        >
                          Fecha Courier:
                        </Typography>
                        <Typography
                          variant='body2'
                          component='span'
                          style={{ marginLeft: '16px' }}
                        >
                          {localCourierDeliveryDate}
                        </Typography>
                      </Box>
                    )}
                    <Typography
                      variant='subtitle1'
                      component='span'
                      style={{
                        minWidth: '120px',
                        fontWeight: 'bold'
                      }}
                    >
                      PICKER:
                    </Typography>
                    <Typography
                      variant='body2'
                      component='span'
                      style={{ marginLeft: '16px' }}
                    >
                      {picker}
                    </Typography>
                  </Grid>
                </Grid>
              </div>
              <Grid
                container
                direction='row'
                justifyContent='space-between'
                alignItems='center'
              >
                <Grid item xs={12} lg={7}>
                  <Box display='flex' alignItems='center' flexWrap='nowrap'>
                    <TextField
                      id='skuId'
                      name='skuId'
                      label='Buscar Producto'
                      autoComplete='off'
                      autoFocus
                      value={sku}
                      variant='outlined'
                      size='small'
                      style={{ minWidth: '200px', flex: isMobile ? 1 : 'none' }}
                      onChange={(e: any) => {
                        handleChangeSku(e)
                        clsAlphaNoOnly(e)
                      }}
                      onKeyPress={(evt: any) => {
                        const keyCode = evt
                          ? evt.which
                            ? evt.which
                            : evt.keyCode
                          : evt.keyCode
                        if (keyCode === 13) {
                          setSkuId(sku)
                        }
                      }}
                      inputProps={{
                        maxLength: 30,
                        pattern: '[a-zA-Z0-9-]'
                      }}
                    />
                    <Box ml={1}>
                      <Button
                        type='submit'
                        variant='contained'
                        color='primary'
                        size='small'
                        onClick={() => {
                          setSkuId(sku)
                        }}
                      >
                        {'BUSCAR'}
                      </Button>
                    </Box>
                    <Box ml={1} display='flex' alignItems='center'>
                      <img
                        src={barCode}
                        alt='codigo de barras'
                        style={{ width: '40px', height: '40px' }}
                      />
                    </Box>
                  </Box>
                </Grid>

                <Grid item xs={12} lg={5}>
                  <Box
                    display='flex'
                    justifyContent={isMobile ? 'center' : 'flex-end'}
                  >
                    <ButtonGroup
                      variant='contained'
                      size='small'
                      style={{
                        marginTop: '8px',
                        width: isMobile ? '100%' : 'auto'
                      }}
                    >
                      <Button
                        className={classes.warning}
                        style={{
                          fontSize: '0.7em',
                          color: 'black',
                          ...(isMobile && { flex: 1 })
                        }}
                        onClick={() => {
                          setSku('')
                          setSkuId('')
                          setBtnFilter('')
                          handlePagination(0)
                        }}
                      >
                        {isMobile ? (
                          <ClearIcon fontSize='small' />
                        ) : (
                          'Limpiar Filtro'
                        )}
                      </Button>
                      <Button
                        className={classes.sucess}
                        style={{
                          fontSize: '0.7em',
                          color: 'black',
                          ...(isMobile && { flex: 1 })
                        }}
                        id='btnCompleto'
                        name='btnCompleto'
                        onClick={() => {
                          setBtnFilter('1-2')
                          handlePagination(0)
                        }}
                      >
                        {isMobile ? (
                          <CheckCircleIcon fontSize='small' />
                        ) : (
                          'Completo'
                        )}
                      </Button>
                      <Button
                        className={classes.difference}
                        style={{
                          fontSize: '0.7em',
                          color: 'black',
                          ...(isMobile && { flex: 1 })
                        }}
                        name='btnDiferencia'
                        id='btnDiferencia'
                        onClick={() => {
                          setBtnFilter('3-4')
                          handlePagination(0)
                        }}
                      >
                        {isMobile ? (
                          <ReportProblemIcon fontSize='small' />
                        ) : (
                          'Con Diferencias'
                        )}
                      </Button>
                      <Button
                        color='secondary'
                        className={classes.manage}
                        style={{
                          fontSize: '0.7em',
                          ...(isMobile && { flex: 1 })
                        }}
                        id='btnGestionado'
                        name='btnGestionado'
                        onClick={() => {
                          setBtnFilter('2-4')
                          handlePagination(0)
                        }}
                      >
                        {isMobile ? (
                          <SettingsIcon fontSize='small' />
                        ) : (
                          'Gestionado'
                        )}
                      </Button>
                    </ButtonGroup>
                  </Box>
                </Grid>
              </Grid>
            </Paper>
          </Box>
        </Fragment>

        <Box mt={2}>
          <Paper elevation={2} style={{ padding: '15px' }}>
            <GenericTableV2<PickingValidateSkuDetail>
              data={rows || []}
              columns={tableColumns}
              keyField='id'
              loading={isLoadingGrid}
              emptyMessage='No se encontro la ruta solicitada'
              actions={tableActions}
              sorting={currentSorting}
              pagination={{
                page: page,
                pageSize: pageSize,
                total: totalItems,
                onPageChange: handlePaginationChange
              }}
              styling={{
                hoverEffect: true,
                rowStyle: (row: PickingValidateSkuDetail) => {
                  if (row.isInRecall) {
                    return { backgroundColor: '#ffebee' }
                  }
                  return {}
                }
              }}
            />
          </Paper>
        </Box>

        <Modal funcClose={handleClose} isOpen={isOpen} size='SM'>
          {isLoadingPrint && <Loading />}
          {!isLoadingPrint && pickingValidatePDF.length > 0 && (
            <Fragment>
              <Typography>{`Informe Ruta Picking N° ${
                pickingHeader.typePickingRouteId === eTypePickingRoute.RTV
                  ? `RTV${pickingRouteId}`
                  : pickingRouteId
              }`}</Typography>
              <DocumentPrint documentPDF={pickingValidatePDF} type='base64' />
            </Fragment>
          )}{' '}
        </Modal>

        <EOSModal
          onClose={handleCloseConfirmation}
          open={isOpenConfirmation}
          maxWidth='sm'
        >
          {isPickingRouteGroupedValidation ? (
            <ConfirmAction
              handleCloseModal={handleCloseConfirmation}
              handleSubmitAction={() => {
                handleClosePickingRouteValidate()
                handleCloseConfirmation()
              }}
              confirmTitle={
                (orderCancellationRequest &&
                  'Recuerde devolver físicamente los productos a sus respectivas posiciones originales.') ||
                `¿Esta seguro que desea cerrar la validación de ruta?`
              }
              confirmText={`Confirmar`}
              negateText={`Cancelar`}
              isLoading={isLoadingConfirm}
            />
          ) : (
            <CloseValidationConfirmationModal
              handleCloseConfirmation={handleCloseConfirmation}
              handleClosePickingRouteValidate={handleClosePickingRouteValidate}
              isPickingRouteGroupedValidation={isPickingRouteGroupedValidation}
              isLoadingConfirm={isLoadingConfirm}
              orderCancellationRequest={orderCancellationRequest}
              pickingRouteId={pickingRouteId}
            />
          )}
        </EOSModal>

        {/* Modales para acciones de la tabla */}
        <EOSModal
          onClose={() => handleCloseModalReportDiff(true)}
          open={showModalReportDiff}
          maxWidth='lg'
          title='Informar Diferencias'
          footer={<PickingDiffereceExplain />}
        >
          {selectedRow && (
            <PickingDiffComponent
              skuDetails={selectedRow}
              orderCancellationRequest={orderCancellationRequest}
              handleCloseModal={handleCloseModalReportDiff}
              typePickingRouteId={typePickingRouteId}
            />
          )}
        </EOSModal>

        <Modal
          funcClose={handleCloseImageModal}
          isOpen={showModalImage}
          size='SM'
        >
          {selectedRow && (
            <CardMedia
              style={{ height: 550 }}
              image={
                selectedRow.urlImage ||
                'https://via.placeholder.com/150/e5e5e5/808080?text=No%20Image'
              }
            />
          )}
        </Modal>

        <Modal
          funcClose={() => handleCloseModalExcessQty(false, '')}
          isOpen={showModalExcessQty}
          size='XS'
        >
          {selectedRow && (
            <PickingValidateExcessQty
              isOpen={showModalExcessQty}
              skuDetails={selectedRow}
              orderCancellationRequest={orderCancellationRequest}
              handleCloseModal={handleCloseModalExcessQty}
            />
          )}
        </Modal>

        {selectedRow && (
          <BatchesModal
            open={showModalBatches}
            onClose={handleCloseBatchesModal}
            batches={selectedRow.batches || []}
            skuName={selectedRow.name}
            pickingRouteDetailId={selectedRow.pickingRouteDetailId}
          />
        )}
      </Container>

      <ProductValidation
        pickingRouteId={pickingRouteId}
        validationModalState={validationModal}
        refreshGrid={() => handleStartPickingRoute()}
      />
    </Fragment>
  )
}
